/**
 * In-race minimap for Weird Kart Racer.
 *
 * A small 2D-canvas overlay that lives inside #hud-root (pointer-events:none)
 * and shows the track outline, this lap's checkpoints and a colored dot for
 * every kart. Like the HUD it is fully self-contained: it injects a guarded
 * <style> block (all classes prefixed "mm-") that reads the shared :root CSS
 * variables.
 *
 * The panel is sized in `rem` (via --mm-size), so it grows with the viewport
 * like the rest of the interface. That makes its pixel size a moving target:
 * the source geometry is kept, and the world->canvas transform plus the backing
 * store are rebuilt whenever the rendered box actually changes. update() —
 * called once per animation frame — then only clears and strokes cached
 * geometry. There is no internal animation loop.
 */
import type { CheckpointState } from './progress';

/** One kart to draw on the minimap. */
export interface MinimapDot {
  /** World X position. */
  x: number;
  /** World Z position. */
  z: number;
  /** Kart color as a 0xRRGGBB integer. */
  color: number;
  /** True for the local player's kart (drawn larger, outlined, on top). */
  self: boolean;
}

/** A point in world XZ. */
interface WorldPoint {
  x: number;
  z: number;
}

/** A projected 2D point in canvas CSS pixels. */
interface Point {
  x: number;
  y: number;
}

const STYLE_ID = 'mm-style';

/** Fallback panel size, in CSS pixels, if --mm-size cannot be resolved. */
const FALLBACK_SIZE = 190;
/** Inset between the track bounds and the panel edge, as a share of the size. */
const PADDING_RATIO = 14 / 190;

/** Tick colours, matching the gates on track and the pips in the HUD. */
const CP_COLORS: Record<CheckpointState, string> = {
  ahead: 'rgba(74, 217, 255, 0.55)',
  next: '#ffcf33',
  passed: '#53e08a',
  missed: '#ff3b3b',
};

const STYLE = `
.mm-canvas {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: var(--mm-size, 11.9rem);
  height: var(--mm-size, 11.9rem);
  background: var(--panel, rgba(18, 22, 38, 0.86));
  border: 1px solid var(--panel-border, rgba(255, 255, 255, 0.12));
  border-radius: 0.9rem;
  box-shadow: 0 0.38rem 1.25rem rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(6px);
  pointer-events: none;
  display: none;
  z-index: 20;
}
.mm-canvas.mm-visible { display: block; }

/* Phones and narrow windows: shrink the panel so the lap counter, timer and
   placing all still fit across the top. --mm-size is overridden in style.css. */
@media (max-width: 860px), (pointer: coarse) {
  .mm-canvas {
    top: calc(0.63rem + env(safe-area-inset-top));
    right: calc(0.63rem + env(safe-area-inset-right));
    border-radius: 0.7rem;
  }
}
`;

/** Inject the minimap stylesheet exactly once per document. */
function ensureStyle(doc: Document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = STYLE;
  doc.head.appendChild(style);
}

/** Convert a 0xRRGGBB integer to a CSS "#rrggbb" string. */
function colorToCss(color: number): string {
  // Mask to 24 bits and zero-pad so e.g. 0x00ff00 renders as "#00ff00".
  const hex = (color & 0xffffff).toString(16).padStart(6, '0');
  return `#${hex}`;
}

export class Minimap {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  // Backing-store scale so lines stay crisp on HiDPI displays. All drawing is
  // done in CSS pixels; a single setTransform() applies this scale.
  private dpr = 1;
  /** Rendered panel size in CSS pixels; follows the root font size. */
  private size = FALLBACK_SIZE;

  // Source geometry, kept so the projection can be rebuilt when the panel
  // changes size (a window dragged to a monitor of a different resolution).
  private centerline: WorldPoint[] = [];
  private startFinishWorld: WorldPoint | null = null;
  private checkpointsWorld: WorldPoint[] = [];
  private checkpointStates: readonly CheckpointState[] = [];

  // Cached projections, rebuilt by reproject(). Null until setTrack().
  private trackPoints: Point[] | null = null;
  private startFinish: Point | null = null;
  private checkpoints: Point[] = [];

  // World->canvas transform: canvasX = (worldX - offsetX) * scale + panX, etc.
  // The z axis is flipped so world +z points up on screen (reads naturally).
  private scale = 1;
  private offsetX = 0;
  private offsetZ = 0;
  private panX = 0;
  private panY = 0;

  constructor(root: HTMLElement) {
    const doc = root.ownerDocument;
    ensureStyle(doc);

    const canvas = doc.createElement('canvas');
    canvas.className = 'mm-canvas';
    // The CSS box is set by the stylesheet in rem; the backing store follows it
    // (and the devicePixelRatio) in resizeBackingStore().
    root.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Minimap: 2D canvas context unavailable');

    this.canvas = canvas;
    this.ctx = ctx;
    this.resizeBackingStore();
  }

  /**
   * Match the backing store to the rendered box and the display density.
   * Returns true when the CSS size changed, i.e. the projection is now stale.
   */
  private resizeBackingStore(): boolean {
    const view = this.canvas.ownerDocument.defaultView;
    this.dpr = Math.max(1, view?.devicePixelRatio ?? 1);

    // offsetWidth is 0 while the panel is hidden; keep the last known size then.
    const rendered = this.canvas.offsetWidth;
    const size = rendered > 0 ? rendered : this.size;
    const resized = size !== this.size;
    this.size = size;

    const px = Math.round(size * this.dpr);
    if (this.canvas.width !== px || this.canvas.height !== px) {
      this.canvas.width = px;
      this.canvas.height = px;
    }
    // Draw in CSS pixel units; the transform bakes in the DPR scale.
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    return resized;
  }

  /**
   * Take the track's geometry and project it. `checkpoints` are in checkpoint
   * index order, so setCheckpointStates() can be indexed the same way.
   */
  setTrack(
    centerline: WorldPoint[],
    startFinish: WorldPoint,
    checkpoints: WorldPoint[] = [],
  ): void {
    this.centerline = centerline.map((p) => ({ x: p.x, z: p.z }));
    this.startFinishWorld = { x: startFinish.x, z: startFinish.z };
    this.checkpointsWorld = checkpoints.map((p) => ({ x: p.x, z: p.z }));
    this.checkpointStates = [];
    this.reproject();
  }

  /** Colour the checkpoint ticks; indexed like the array given to setTrack(). */
  setCheckpointStates(states: readonly CheckpointState[]): void {
    this.checkpointStates = states;
  }

  /**
   * Recompute the world->canvas transform and the cached projections. The
   * transform preserves aspect ratio (uniform scale) and centers the track
   * within the padded panel.
   */
  private reproject(): void {
    if (this.centerline.length === 0) {
      this.trackPoints = null;
      this.startFinish = null;
      this.checkpoints = [];
      return;
    }

    // XZ bounding box of the centerline.
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const p of this.centerline) {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.z < minZ) minZ = p.z;
      if (p.z > maxZ) maxZ = p.z;
    }

    const spanX = maxX - minX;
    const spanZ = maxZ - minZ;
    const padding = this.size * PADDING_RATIO;
    const inner = this.size - 2 * padding; // Drawable area inside the padding.

    // Uniform scale that fits the larger span into the inner area. Guard against
    // degenerate (zero-span) tracks so scale stays finite.
    const denom = Math.max(spanX, spanZ);
    this.scale = denom > 0 ? inner / denom : 1;

    // World coordinates of the box corner used as the projection origin.
    this.offsetX = minX;
    this.offsetZ = minZ;

    // Center the (possibly non-square) track within the inner area.
    this.panX = padding + (inner - spanX * this.scale) / 2;
    this.panY = padding + (inner - spanZ * this.scale) / 2;

    this.trackPoints = this.centerline.map((p) => this.project(p.x, p.z));
    this.startFinish = this.startFinishWorld
      ? this.project(this.startFinishWorld.x, this.startFinishWorld.z)
      : null;
    this.checkpoints = this.checkpointsWorld.map((p) => this.project(p.x, p.z));
  }

  /** Map a world (x, z) to canvas CSS pixels, flipping z so +z reads as "up". */
  private project(x: number, z: number): Point {
    return {
      x: (x - this.offsetX) * this.scale + this.panX,
      // Flip the vertical axis: larger world z ends up nearer the top.
      y: this.size - ((z - this.offsetZ) * this.scale + this.panY),
    };
  }

  /**
   * Render one frame: clear, draw the cached track as a rounded "road" polyline,
   * draw the checkpoints and start/finish marker, then draw each kart dot. Does
   * nothing until setTrack() has been called.
   */
  update(dots: MinimapDot[]): void {
    // Cheap check: keeps the map crisp and correctly scaled when the display
    // density or the root font size changes underneath us.
    if (this.resizeBackingStore()) this.reproject();

    const points = this.trackPoints;
    if (!points || points.length === 0) return;

    // Everything is authored against a 190px panel and scaled from there, so the
    // map reads the same whatever size the viewport gives it.
    const k = this.size / FALLBACK_SIZE;
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.size, this.size);

    // Trace the (closed) track path once, then stroke it twice for a road look.
    this.traceTrack(ctx, points);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    // Wide light-gray base = the road surface.
    ctx.strokeStyle = 'rgba(210, 214, 232, 0.75)';
    ctx.lineWidth = 8 * k;
    ctx.stroke();

    // Narrower darker line on top = the center of the road.
    ctx.strokeStyle = 'rgba(120, 126, 150, 0.9)';
    ctx.lineWidth = 5 * k;
    ctx.stroke();

    this.drawCheckpoints(ctx, k);

    // Start/finish: a small marker, tinted when it is the gate to head for.
    if (this.startFinish) {
      const state = this.checkpointStates[0];
      ctx.beginPath();
      ctx.arc(this.startFinish.x, this.startFinish.y, 3.5 * k, 0, Math.PI * 2);
      ctx.fillStyle = state === 'next' || state === 'missed' ? CP_COLORS[state] : '#ffffff';
      ctx.fill();
      ctx.lineWidth = 1.5 * k;
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.stroke();
    }

    // Non-self dots first so the local player's dot always sits on top.
    let selfDot: MinimapDot | null = null;
    for (const dot of dots) {
      if (dot.self) {
        selfDot = dot;
        continue;
      }
      const p = this.project(dot.x, dot.z);
      ctx.beginPath();
      ctx.arc(p.x, p.y, 4 * k, 0, Math.PI * 2);
      ctx.fillStyle = colorToCss(dot.color);
      ctx.fill();
    }

    // The local player's dot: larger, with a white outline, drawn last.
    if (selfDot) {
      const p = this.project(selfDot.x, selfDot.z);
      ctx.beginPath();
      ctx.arc(p.x, p.y, 6 * k, 0, Math.PI * 2);
      ctx.fillStyle = colorToCss(selfDot.color);
      ctx.fill();
      ctx.lineWidth = 2 * k;
      ctx.strokeStyle = '#ffffff';
      ctx.stroke();
    }
  }

  /**
   * Checkpoint ticks. The one to head for is ringed and the ones driven around
   * are red, which is the whole reason they are here: on a circuit that folds
   * back on itself, "go back for it" needs a direction.
   */
  private drawCheckpoints(ctx: CanvasRenderingContext2D, k: number): void {
    for (let i = 1; i < this.checkpoints.length; i++) {
      const p = this.checkpoints[i];
      const state = this.checkpointStates[i] ?? 'ahead';
      const highlight = state === 'next' || state === 'missed';
      ctx.beginPath();
      ctx.arc(p.x, p.y, (highlight ? 3.4 : 2.2) * k, 0, Math.PI * 2);
      ctx.fillStyle = CP_COLORS[state];
      ctx.fill();
      if (!highlight) continue;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 5.6 * k, 0, Math.PI * 2);
      ctx.lineWidth = 1.4 * k;
      ctx.strokeStyle = CP_COLORS[state];
      ctx.stroke();
    }
  }

  /** Build the closed track path on the context (does not stroke it). */
  private traceTrack(ctx: CanvasRenderingContext2D, points: Point[]): void {
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.closePath();
  }

  /** Reveal the minimap. */
  show(): void {
    this.canvas.classList.add('mm-visible');
    // It was display:none until now, so this is the first chance to measure it.
    if (this.resizeBackingStore()) this.reproject();
  }

  /** Hide the minimap (keeps cached geometry). */
  hide(): void {
    this.canvas.classList.remove('mm-visible');
  }
}
